/**
 * @fileoverview Song Review Auto-Save Middleware
 *
 * Automatically saves song review session state to Convex after grade submissions.
 * Implements debounced saves to avoid excessive database writes.
 *
 * **How it works:**
 * 1. Listens for submitGrade and completeSongReviewSession actions
 * 2. Debounces saves by 2 seconds (batches multiple rapid grades)
 * 3. Dispatches async thunk to save session to Convex
 * 4. Marks session as saved after successful save
 *
 * **Save Strategy:**
 * - Partial sessions: Save sessionId, grades, currentIndex
 * - Completed sessions: Submit full song review via submitSongReview
 * - Interrupted sessions: Can resume from sessionId on reload
 *
 * @module store/middleware/songReviewAutoSaveMiddleware
 */

import type { Middleware } from '@reduxjs/toolkit'
import { isAnyOf } from '@reduxjs/toolkit'
import {
  submitGrade,
  completeSongReviewSession,
  type AnkiGrade,
  type SongReviewSessionState,
} from '../slices/songReviewSessionSlice'

// Debounce timer reference (module-level to persist across actions)
let saveTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Auto-save middleware for song review sessions
 * Debounces save triggers to prevent excessive database writes.
 * The component watches selectSessionNeedsSave and performs actual Convex save.
 */
export const songReviewAutoSaveMiddleware: Middleware =
  (store) => (next) => (action) => {
    // First, let the action proceed (update state)
    const result = next(action)

    // Check if this is an action that requires auto-save
    if (isAnyOf(submitGrade, completeSongReviewSession)(action)) {
      // Clear existing debounce timer
      if (saveTimer) {
        clearTimeout(saveTimer)
      }

      // Debounce by 2 seconds - just restart the timer
      // The component watches selectSessionNeedsSave (which checks !isSaved)
      // and will trigger the actual save after this debounce period
      saveTimer = setTimeout(() => {
        const state = store.getState()
        const session = state.songReviewSession

        if (session.isActive && !session.isSaved) {
          console.log('[Auto-Save] Debounce complete, component will save session:', session.sessionId)
          // Component's useEffect will trigger when needsSave is true
          // Component handles actual save and marks as saved after success
        }
      }, 2000) // 2 second debounce
    }

    return result
  }

/**
 * Helper function to build flashcard grades payload for submitSongReview
 * NOTE: This is exported for use in React components, not called from middleware
 */
export function buildSongReviewPayload(session: SongReviewSessionState) {
  if (!session.songId || !session.sessionId) {
    throw new Error('Cannot build payload: session not initialized')
  }

  const flashcardGrades: Array<{
    flashcardId: string
    grade: AnkiGrade
  }> = session.grades.map((g: { flashcardId: string; grade: AnkiGrade }) => ({
    flashcardId: g.flashcardId,
    grade: g.grade,
  }))

  return {
    songId: session.songId,
    flashcardGrades,
    sessionId: session.sessionId,
  }
}
